import { computed } from 'vue'
import { freezeObject, isFunction } from '@/global/utils/utils'
import { api } from '@/global/services/api'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { showDetailsAction, deleteAction } from '@/global/components/table/helpers/tableActions'

const TABLE_KEY = 'activities'
const UI_VIEW = 'temporary_regulations_activities'

export function useTable (options) {
  const {
    trans,
    openActivityDialogFn,
    openInfoDialogFn,
    closeInfoDialogFn
  } = options || {}

  const fetchTableData = () => {
    const { fetchData } = registeredTableHandlers?.[TABLE_KEY] || {}
    if (!isFunction(fetchData)) {
      return
    }
    fetchData()
  }

  const onCreateNewActivity = () => {
    openActivityDialogFn({
      title: trans('road-maintenance/work_orders.creating_activity'),
      editId: null
    })
  }

  const onShowDetails = (event) => {
    openActivityDialogFn({
      title: trans('road-maintenance/work_orders.updating_activity'),
      editId: event?.data?.id
    })
  }

  const deleteActivityHandler = async (event) => {
    try {
      const { id } = event?.data || {}
      if (!id) {
        return
      }

      await api('road-maintenance').delete(`activities/${id}`)
      fetchTableData()
      closeInfoDialogFn()
    }
    catch (exception) {
      console.error(exception)
    }
  }

  const onActivityDelete = (event) => {
    const { name } = event?.data || {}
    const nameTitle = name?.length > 20
      ? name.substring(0, 20) + '...'
      : name || ''

    openInfoDialogFn({
      title: trans('road-maintenance/work_orders.activity_deletion_confirmation_title') + (nameTitle ? ' - ' + nameTitle : ''),
      message: trans('road-maintenance/work_orders.activity_deletion_confirmation_message'),
      onConfirm: () => deleteActivityHandler(event)
    })
  }

  const apiConfig = computed(() => ({
    get: {
      module: 'road-maintenance',
      route: 'activities'
    }
  }))

  const cellsConfig = freezeObject({
    type: {
      custom_display_value: (data) => {
        return data?.type?.description ? trans(data.type.description) : ''
      }
    }
  })

  const headers = computed(() => ([
    {
      label: trans('road-maintenance/settings.id'),
      key: 'id',
      always_visible: true
    },
    {
      label: trans('road-maintenance/aggregator.name'),
      key: 'name',
      always_visible: true
    },
    {
      label: trans('road-maintenance/patrol-service.description'),
      key: 'description',
      always_visible: true
    },
    {
      label: trans('road-maintenance/work_orders.activity_type'),
      key: 'type',
      always_visible: true
    }
  ]))

  const actionsConfig = computed(() => ([
    showDetailsAction(trans, onShowDetails),
    deleteAction(trans, onActivityDelete)
  ]))

  const paginationConfig = freezeObject({
    perPageOptions: [50, 100],
    perPage: 50,
    currentPage: 1
  })

  const breadcrumbs = computed(() => ({
    items: [
      {
        text: trans('road-maintenance.settings')
      },
      {
        text: trans('road-maintenance/work_orders.activities'),
        bolded: true
      }
    ]
  }))

  const config = computed(() => ({
    tableConfig: {
      key: TABLE_KEY,
      uiView: UI_VIEW,
      apiConfig: apiConfig.value,
      filters_and_columns_visibility_config: {
        showFiltersButton: false,
        newBtnPosition: 'start',
        newClick: onCreateNewActivity
      },
      rowDblClick: onShowDetails,
      tableConfig: {
        cellsConfig,
        headers: headers.value,
        actionsConfig: actionsConfig.value,
        paginationConfig
      }
    },
    breadcrumbs: breadcrumbs.value
  }))

  return {
    config,
    fetchTableData
  }
}
